import { FC, useEffect, useState, useMemo } from 'react';
import { DateTime } from 'luxon';
import { orderBy } from 'natural-orderby';
import { CircularProgress, Tooltip } from '@mui/material';
import DeleteModalForm from 'src/components/Items/DeleteModalForm';
import Table from 'src/components/Items/table';
import SeasonPriceModal from 'src/pages/Admin/Content/Modals/SeasonPriceModal';
import CustomCheckbox from 'src/components/Items/button/CustomCheckbox';
import FormIcon from 'src/components/Items/ActionForm/FormIcon';
import { useActions } from 'src/hooks/useActions';
import { useTypedSelector } from 'src/hooks/useTypeSelector';
import useShowSnackbar from 'src/hooks/useShowSnackbar';
import { ISeasonPriceFetch } from 'src/models/ISeasonPrice';
import Icons from 'src/common/icons';

interface IProps {
  isDeleteSP: boolean;
  SPforDelete: number[];
  setSPforDelete: (sps: number[]) => void;
}

const SeasonPriceTable: FC<IProps> = ({ isDeleteSP, SPforDelete, setSPforDelete }) => {
  const { getSeasonPrice, clearSeasonPriceResult } = useActions();
  const {
    seasonPrice: { seasonPriceList, isLoading, result }
  } = useTypedSelector(state => state);
  useShowSnackbar(isLoading, result, clearSeasonPriceResult, 'Season price');

  const [selectedSeasonPrice, setSelectedSeasonPrice] = useState({} as ISeasonPriceFetch);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  useEffect(() => {
    getSeasonPrice();
  }, []);

  useEffect(() => {
    (result.add || result.update || result.delete) && getSeasonPrice();
  }, [result.add, result.update, result.delete]);

  const handleOpenEdit = (order: ISeasonPriceFetch) => {
    setOpenEdit(true);
    setSelectedSeasonPrice(order);
  };

  const handleOpenDelete = (order: ISeasonPriceFetch) => {
    setOpenDelete(true);
    setSelectedSeasonPrice(order);
  };

  const toggleCheckbox = (id: number) => {
    const isExist = SPforDelete.some(el => el === id);

    if (isExist) {
      const newArray = SPforDelete.filter(el => el !== id);
      setSPforDelete(newArray);
    } else {
      const newArray = [...SPforDelete, id];
      setSPforDelete(newArray);
    }
  };

  const sortedSeasonPrice = orderBy(
    seasonPriceList,
    [seasonPrice => seasonPrice.location],
    ['asc', 'asc']
  );

  const columns = useMemo(
    () => [
      {
        Header: 'Location',
        accessor: 'location',
        Cell: ({
          row: {
            original: { location }
          }
        }: any) => {
          return <>{location}</>;
        }
      },
      {
        Header: 'Start',
        accessor: 'start_date',
        Cell: ({
          row: {
            original: { start_date, timeOffset }
          }
        }) => (
          <>{`${DateTime.fromISO(start_date, { zone: 'UTC' })
            .plus({ minutes: timeOffset })
            .toFormat('DD')}`}</>
        )
      },
      {
        Header: 'End',
        accessor: 'end_date',
        Cell: ({
          row: {
            original: { end_date, timeOffset }
          }
        }) => (
          <>{`${DateTime.fromISO(end_date, { zone: 'UTC' })
            .plus({ minutes: timeOffset })
            .toFormat('DD')}`}</>
        )
      },
      {
        Header: 'Rate',
        accessor: 'rate',
        Cell: ({
          row: {
            original: { rate }
          }
        }: any) => {
          return <>{rate}%</>;
        }
      },
      {
        accessor: 'actions',
        Cell: ({ row: { original } }: any) => (
          <>
            <div className='flex_CC' style={{ justifyContent: 'right' }}>
              <div className='action-wrapper'>
                <Tooltip title='Edit season price'>
                  <FormIcon
                    icon={<Icons.Edit classes='icon_stroke' />}
                    handleClick={() => handleOpenEdit(original)}
                    title='Edit season price'
                  />
                </Tooltip>
                <Tooltip title='Delete season price'>
                  {isDeleteSP ? (
                    <div>
                      <CustomCheckbox
                        onChange={() => toggleCheckbox(original.id)}
                        checked={SPforDelete.some(el => el === original.id)}
                        name='select for delete'
                      />
                    </div>
                  ) : (
                    <FormIcon
                      icon={<Icons.Delete classes='icon_stroke' />}
                      handleClick={() => handleOpenDelete(original)}
                      title='Delete season price'
                    />
                  )}
                </Tooltip>
              </div>
            </div>
          </>
        )
      }
    ],
    [seasonPriceList, isDeleteSP, SPforDelete]
  );

  return (
    <>
      <SeasonPriceModal
        open={openEdit}
        editSeasonPrice={selectedSeasonPrice}
        handleClose={() => setOpenEdit(false)}
      />
      <DeleteModalForm
        id={[selectedSeasonPrice.id]}
        type='season price'
        handleClose={() => setOpenDelete(false)}
        open={openDelete}
      />
      {isLoading.all ? (
        <CircularProgress
          size={200}
          style={{ position: 'fixed', top: '40%', left: '45%', zIndex: 100 }}
        />
      ) : seasonPriceList.length ? (
        <div className='order-table'>
          <Table.Base data={sortedSeasonPrice} columns={columns} class='react-table' total={0} />
        </div>
      ) : null}
    </>
  );
};
export default SeasonPriceTable;
